import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useParams, Navigate, useLocation } from "react-router-dom";
import { Form, Table, Button } from "react-bootstrap";
import { Icon } from "react-icons-kit";
import { ic_arrow_back, ic_delete_outline } from "react-icons-kit/md";
import MainWrapper from "../../MainWrapper";
import Title from "../../elements/Title";
import CustomButton from "../../elements/CustomButton";
import { toast } from "react-toastify";
import {
  CC_ESTIMATE_SAVE_REQUEST,
  CC_ESTIMATE_LOAD_REQUEST,
} from "../../../_utils/constants/CountertopCabinetEstimate";
import { CUSTOMER_LIST_REQUEST } from "../../../_utils/constants/Customer";
import { buildEstimatePayload, hydrateEstimate } from "../../../_utils/manualEstimatePayload";
import { deriveCountertopCabinetPermissions } from "../../../_utils/countertopCabinetPermissions";
import AddScopeModal from "./models/AddScopeModal";
import { formatCurrency } from "../../../_utils/manualQuantityScopes";
import {
  recalcLinePrice,
  lineExtended,
  sectionTotals,
  estimateItemsTotal,
  validateEstimateItems,
  upsertUnique,
  materialIdentityKey,
  laborIdentityKey,
} from "../../../_utils/manualEstimateItems";

// ---------------------------------------------------------------------------
// Presentational form (no Redux / no Router) — exported for straightforward testing.
//
// The estimate is a flat list of MATERIAL and LABOR line items in the OrderConfirmation shape
// (quantity, unit, privateName/style/color, unitPrice, linePrice, gpm, lineCost, laborCategory,
// labor, ...). Rows are pulled in via the purpose-built "Add Scope" modal — the parent opens it
// through the injected onAddScope callback (pick a C/C structure -> check products + hand-key price
// -> kit items) and feeds the returned rows back in through incomingItems (append by token).
// Everything is manually editable; no graphical/floorplan/geometry logic lives here.
// ---------------------------------------------------------------------------
export const ScopeEstimateForm = ({
  initialMaterial = [],
  initialLabor = [],
  isSaving = false,
  onSave,
  onAddScope,
  // Rows merged in from the "Add Scope" modal. The parent bumps `token` after each modal close;
  // the form appends the new material/labor without disturbing rows the user is already editing.
  incomingItems = { material: [], labor: [], token: 0 },
  // Permissions — reused from existing RBAC; default open so standalone use/tests work.
  canEdit = true,
}) => {
  const readOnly = !canEdit;
  const [materialList, setMaterialList] = useState(
    Array.isArray(initialMaterial) ? initialMaterial : []
  );
  const [laborList, setLaborList] = useState(Array.isArray(initialLabor) ? initialLabor : []);
  const [errors, setErrors] = useState(null);

  // Append rows merged in from the "Add Scope" modal whenever the parent bumps the token.
  const lastIncomingToken = useRef(incomingItems?.token || 0);
  useEffect(() => {
    const token = incomingItems?.token || 0;
    if (token && token !== lastIncomingToken.current) {
      lastIncomingToken.current = token;
      const newMaterial = Array.isArray(incomingItems.material) ? incomingItems.material : [];
      const newLabor = Array.isArray(incomingItems.labor) ? incomingItems.labor : [];
      // The same structure/product can be picked in a later Add Scope run; de-dupe by identity and
      // let the latest selection replace the existing line (in place) instead of duplicating it.
      if (newMaterial.length)
        setMaterialList((prev) => upsertUnique(prev, newMaterial, materialIdentityKey));
      if (newLabor.length) setLaborList((prev) => upsertUnique(prev, newLabor, laborIdentityKey));
    }
  }, [incomingItems]);

  // Edit a single line's field and recompute its line price (qty x unit price).
  const editLine = (setList, index, field, value) =>
    setList((prev) =>
      prev.map((item, i) => (i === index ? recalcLinePrice({ ...item, [field]: value }) : item))
    );
  // Quantity can't be negative — clamp any negative entry to 0.
  const clampQty = (field, value) =>
    field === "quantity" && String(value).trim() !== "" && Number(value) < 0 ? "0" : value;
  const handleMaterialField = (index, field, value) =>
    editLine(setMaterialList, index, field, clampQty(field, value));
  const handleLaborField = (index, field, value) =>
    editLine(setLaborList, index, field, clampQty(field, value));
  const handleDeleteMaterial = (index) =>
    setMaterialList((prev) => prev.filter((_, i) => i !== index));
  const handleDeleteLabor = (index) => setLaborList((prev) => prev.filter((_, i) => i !== index));

  const validate = () => {
    // Every material + labor line must have a valid qty and price before Save.
    const items = validateEstimateItems(materialList, laborList);
    const result = { material: items.material, labor: items.labor };
    result.items = !items.isValid ? "Enter a valid quantity and price for every line item." : "";
    result.isValid = items.isValid;
    return result;
  };

  const handleSave = (evt) => {
    evt.preventDefault();
    const result = validate();
    setErrors(result);
    if (!result.isValid) return;
    if (onSave) {
      onSave({
        materialList,
        laborList,
        total: estimateItemsTotal(materialList, laborList),
      });
    }
  };

  const matTotals = sectionTotals(materialList);
  const labTotals = sectionTotals(laborList);
  const gpmText = (n) => `${(Number.isFinite(n) ? n : 0).toFixed(2)}%`;
  const showLinePrice = (item) =>
    formatCurrency(String(item.linePrice ?? "").trim() !== "" ? item.linePrice : lineExtended(item));

  return (
    <Form noValidate onSubmit={handleSave}>
      {!readOnly ? (
        <div className="mb-3">
          <CustomButton
            variant="success"
            type="button"
            className="btn-add-scope"
            handleClick={() => onAddScope && onAddScope()}
          >
            + Add Scope
          </CustomButton>
        </div>
      ) : null}

      {/* Material section */}
      <h5 className="mt-4 mb-2">Material</h5>
      <Table bordered responsive className="mb-1">
        <thead>
          <tr>
            <th style={{ width: "110px" }}>Qty</th>
            <th>Unit</th>
            <th>Private Name</th>
            <th>Style</th>
            <th>Color</th>
            <th style={{ width: "120px" }}>Unit Price</th>
            <th style={{ width: "120px" }}>Line Price</th>
            {!readOnly ? <th style={{ width: "70px" }}>Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {materialList.length === 0 ? (
            <tr>
              <td colSpan={readOnly ? 7 : 8} className="text-muted">
                No material items. Use &quot;Add New Material Item&quot; to pull in products.
              </td>
            </tr>
          ) : (
            materialList.map((item, index) => (
              <tr key={index}>
                <td>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    aria-label={`Material ${index + 1} quantity`}
                    value={item.quantity ?? ""}
                    disabled={readOnly}
                    isInvalid={!!errors?.material?.[index]?.quantity}
                    onChange={(e) => handleMaterialField(index, "quantity", e.target.value)}
                  />
                  {errors?.material?.[index]?.quantity ? (
                    <div className="text-danger small mt-1">{errors.material[index].quantity}</div>
                  ) : null}
                </td>
                <td>{item.unit || item.unitName || ""}</td>
                <td>{item.privateName || ""}</td>
                <td>{item.style || item.product || ""}</td>
                <td>{item.color || ""}</td>
                <td>
                  <Form.Control
                    type="number"
                    step="0.01"
                    aria-label={`Material ${index + 1} unit price`}
                    value={item.unitPrice ?? item.price ?? ""}
                    disabled={readOnly}
                    isInvalid={!!errors?.material?.[index]?.unitPrice}
                    onChange={(e) => handleMaterialField(index, "unitPrice", e.target.value)}
                  />
                  {errors?.material?.[index]?.unitPrice ? (
                    <div className="text-danger small mt-1">{errors.material[index].unitPrice}</div>
                  ) : null}
                </td>
                <td data-testid={`material-line-${index}`}>${showLinePrice(item)}</td>
                {!readOnly ? (
                  <td>
                    <Button
                      variant="link"
                      type="button"
                      className="text-danger p-0"
                      aria-label={`Delete material ${index + 1}`}
                      onClick={() => handleDeleteMaterial(index)}
                    >
                      <Icon size={20} icon={ic_delete_outline} />
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))
          )}
          <tr>
            <td colSpan={5} className="text-end fw-bold">Total</td>
            <td className="fw-bold">${formatCurrency(matTotals.unitPriceTotal)}</td>
            <td className="fw-bold" data-testid="material-total">${formatCurrency(matTotals.linePriceTotal)}</td>
            {!readOnly ? <td></td> : null}
          </tr>
        </tbody>
      </Table>
      <div className="d-flex flex-wrap justify-content-between gap-3 mb-2 fw-bold bg-light p-2 rounded">
        <span>Material Cost: ${formatCurrency(matTotals.cost)}</span>
        <span>Material Profit: ${formatCurrency(matTotals.profit)}</span>
        <span>Material GPM: {gpmText(matTotals.gpm)}</span>
      </div>

      {/* Labor section */}
      <h5 className="mt-4 mb-2">Labor</h5>
      <Table bordered responsive className="mb-1">
        <thead>
          <tr>
            <th style={{ width: "110px" }}>Qty</th>
            <th>Unit</th>
            <th>Labor Category</th>
            <th>Labor Name</th>
            <th style={{ width: "120px" }}>Unit Price</th>
            <th style={{ width: "120px" }}>Line Price</th>
            {!readOnly ? <th style={{ width: "70px" }}>Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {laborList.length === 0 ? (
            <tr>
              <td colSpan={readOnly ? 6 : 7} className="text-muted">
                No labor items. Use &quot;Add New Labor Item&quot; to pull in labor.
              </td>
            </tr>
          ) : (
            laborList.map((item, index) => (
              <tr key={index}>
                <td>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    aria-label={`Labor ${index + 1} quantity`}
                    value={item.quantity ?? ""}
                    disabled={readOnly}
                    isInvalid={!!errors?.labor?.[index]?.quantity}
                    onChange={(e) => handleLaborField(index, "quantity", e.target.value)}
                  />
                  {errors?.labor?.[index]?.quantity ? (
                    <div className="text-danger small mt-1">{errors.labor[index].quantity}</div>
                  ) : null}
                </td>
                <td>{item.unit || item.unitName || ""}</td>
                <td>{item.laborCategory || ""}</td>
                <td>{item.labor || item.laborName || ""}</td>
                <td>
                  <Form.Control
                    type="number"
                    step="0.01"
                    aria-label={`Labor ${index + 1} unit price`}
                    value={item.unitPrice ?? item.price ?? ""}
                    disabled={readOnly}
                    isInvalid={!!errors?.labor?.[index]?.unitPrice}
                    onChange={(e) => handleLaborField(index, "unitPrice", e.target.value)}
                  />
                  {errors?.labor?.[index]?.unitPrice ? (
                    <div className="text-danger small mt-1">{errors.labor[index].unitPrice}</div>
                  ) : null}
                </td>
                <td data-testid={`labor-line-${index}`}>${showLinePrice(item)}</td>
                {!readOnly ? (
                  <td>
                    <Button
                      variant="link"
                      type="button"
                      className="text-danger p-0"
                      aria-label={`Delete labor ${index + 1}`}
                      onClick={() => handleDeleteLabor(index)}
                    >
                      <Icon size={20} icon={ic_delete_outline} />
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))
          )}
          <tr>
            <td colSpan={4} className="text-end fw-bold">Total</td>
            <td className="fw-bold">${formatCurrency(labTotals.unitPriceTotal)}</td>
            <td className="fw-bold" data-testid="labor-total">${formatCurrency(labTotals.linePriceTotal)}</td>
            {!readOnly ? <td></td> : null}
          </tr>
        </tbody>
      </Table>
      <div className="d-flex flex-wrap justify-content-between gap-3 mb-2 fw-bold bg-light p-2 rounded">
        <span>Labor Cost: ${formatCurrency(labTotals.cost)}</span>
        <span>Labor Profit: ${formatCurrency(labTotals.profit)}</span>
        <span>Labor GPM: {gpmText(labTotals.gpm)}</span>
      </div>

      <div className="d-flex justify-content-end align-items-center mb-3">
        <span className="me-2">Estimate Total:</span>
        <strong data-testid="estimate-total">
          ${formatCurrency(estimateItemsTotal(materialList, laborList))}
        </strong>
      </div>

      {errors?.items ? (
        <div className="text-danger small mb-2 text-end" data-testid="items-error">{errors.items}</div>
      ) : null}

      <div className="mt-4 d-flex flex-wrap align-items-end gap-2">
        {!readOnly ? (
          <CustomButton variant="primary" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </CustomButton>
        ) : (
          <span className="text-muted">You do not have permission to edit this estimate.</span>
        )}
      </div>
    </Form>
  );
};

// ---------------------------------------------------------------------------
// Connected page — Countertops/Cabinets manual quantity calculator route target.
//
// Renders the Material/Labor grids + Save, fetches the customer (for storeId), and opens
// the purpose-built AddScopeModal. The modal returns { material, labor } (Qty blank) which is merged
// into the grids via the `incoming` token; the estimator keys quantities and Saves (draft).
// ---------------------------------------------------------------------------
const CountertopCabinetQuantity = ({
  auth: { auth, isAuthenticated },
  countertopCabinetEstimate,
  customerList = [],
  canView = true,
  canEdit = true,
  saveEstimate,
  loadEstimate,
  fetchCustomerList,
}) => {
  const { contactInfoId, unitTypeId, variantId } = useParams();
  const location = useLocation();

  const { estimate, isSaving, isSaved, isLoaded, errors } = countertopCabinetEstimate || {};

  const estimateIdRef = useRef(0);
  const prevSavingRef = useRef(isSaving);
  // The customer's store (fetched by contactInfoId) anchors both the modal's store-scoped product
  // filtering and the estimate payload. A loaded estimate's own storeId takes precedence.
  const customerStoreId = customerList?.[0]?.storeId ?? 0;
  const storeId = estimate?.result?.storeId ?? estimate?.storeId ?? customerStoreId ?? 0;

  // Header labels: the customer detail page passes the property + floor-plan NAMES via route state
  // when opening this page. Fall back to the fetched customer's property name / the ids on refresh.
  const propertyName =
    location.state?.propertyName || customerList?.[0]?.shipToPropertyName || `customer #${contactInfoId}`;
  const floorPlanName = location.state?.floorPlanName || `#${unitTypeId}`;

  const [formKey, setFormKey] = useState(0);
  const [initialForm, setInitialForm] = useState({ material: [], labor: [] });

  // "Add Scope" modal state + the rows it feeds back into the grids (bumped token = new merge).
  const [addScopeOpen, setAddScopeOpen] = useState(false);
  const [incoming, setIncoming] = useState({ material: [], labor: [], token: 0 });
  const incomingTokenRef = useRef(0);

  const hasRequiredParams = Boolean(contactInfoId) && Boolean(unitTypeId);

  // On mount: load the customer (for storeId) and any saved estimate.
  useEffect(() => {
    if (isAuthenticated && auth?.[0]?.token) {
      if (contactInfoId) {
        fetchCustomerList(auth[0].token, "asc", 1, 1, { contactInfoId }, auth[0].userId, false, false, false);
      }
      if (hasRequiredParams) {
        loadEstimate(auth[0].token, contactInfoId, unitTypeId, variantId || 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // When a saved estimate arrives, restore every entered value.
  useEffect(() => {
    if (isLoaded && estimate) {
      const hydrated = hydrateEstimate(estimate);
      estimateIdRef.current = hydrated.estimateId || 0;
      setInitialForm({
        material: hydrated.materialList || [],
        labor: hydrated.laborList || [],
      });
      setFormKey((k) => k + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, estimate]);

  // Success / failure notification on the save transition.
  useEffect(() => {
    const wasSaving = prevSavingRef.current;
    prevSavingRef.current = isSaving;
    if (wasSaving && !isSaving) {
      if (isSaved) {
        toast.success(`Quantity has been saved for ${floorPlanName}`);
        loadEstimate(auth[0].token, contactInfoId, unitTypeId, variantId || 0);
      } else if (errors && Object.keys(errors).length > 0) {
        toast.error("Failed to save quantity. Please try again.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaving]);

  // "Add Scope" opens the purpose-built modal (pick a C/C structure -> check products + hand-key
  // price -> kit items for kitted products). On "Add" it returns { material, labor } (Qty blank),
  // which we merge into the grids via the `incoming` token.
  const handleAddScope = () => setAddScopeOpen(true);

  const handleScopeAdd = ({ material = [], labor = [] } = {}) => {
    if (material.length || labor.length) {
      incomingTokenRef.current += 1;
      setIncoming({ material, labor, token: incomingTokenRef.current });
    }
    setAddScopeOpen(false);
  };

  if (!isAuthenticated) return <Navigate to="/login" />;

  const handleSave = ({ materialList, laborList }) => {
    const payload = buildEstimatePayload(
      { description: "", materialList, laborList },
      {
        estimateId: estimateIdRef.current,
        contactInfoId,
        storeId,
        unitTypeId,
        variantId: variantId || 0,
      }
    );
    saveEstimate(auth[0].token, payload);
  };

  return (
    <MainWrapper>
      <div className="container-fluid">
        <div className="sub-header mt-5 mb-3">
          <Title>Countertop / Cabinet Quantity Calculator</Title>
          <Link
            className="btn btn-primary me-1"
            to={hasRequiredParams ? `/customer-list/customer-detail/${contactInfoId}` : "/customer-list"}
          >
            <Icon icon={ic_arrow_back} />Back
          </Link>
        </div>

        {!canView ? (
          <div className="alert alert-danger" role="alert">
            You do not have permission to view this estimate.
          </div>
        ) : hasRequiredParams ? (
          <>
            <p className="text-muted">
              {propertyName} - floor plan {floorPlanName}
            </p>
            <ScopeEstimateForm
              key={formKey}
              initialMaterial={initialForm.material}
              initialLabor={initialForm.labor}
              isSaving={isSaving}
              onSave={handleSave}
              onAddScope={handleAddScope}
              incomingItems={incoming}
              canEdit={canEdit}
            />
            {addScopeOpen ? (
              <AddScopeModal
                show={addScopeOpen}
                onClose={() => setAddScopeOpen(false)}
                onAdd={handleScopeAdd}
                storeId={storeId}
                contactInfoId={contactInfoId}
              />
            ) : null}
          </>
        ) : (
          <div className="alert alert-warning" role="alert">
            Missing customer or floor plan reference. Please return to the customer detail and
            select a floor plan from the Quantity Calculator.
          </div>
        )}
      </div>
    </MainWrapper>
  );
};

const mapStateToProps = (state) => {
  const perms = deriveCountertopCabinetPermissions(state.Admin?.userMenuList);
  return {
    auth: state.auth,
    countertopCabinetEstimate: state.countertopCabinetEstimate,
    customerList: state.customerInfo?.customerList || [],
    canView: perms.canView,
    canEdit: perms.canEdit,
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchCustomerList: (token, sort, page, paginate, listParamState, userId, salesPersonId, GrpPcId, isChecked) =>
    dispatch({ type: CUSTOMER_LIST_REQUEST, token, sort, page, paginate, listParamState, userId, salesPersonId, GrpPcId, isChecked }),
  saveEstimate: (token, payload) => dispatch({ type: CC_ESTIMATE_SAVE_REQUEST, token, payload }),
  loadEstimate: (token, contactInfoId, unitTypeId, variantId) =>
    dispatch({ type: CC_ESTIMATE_LOAD_REQUEST, token, contactInfoId, unitTypeId, variantId }),
});

export default connect(mapStateToProps, mapDispatchToProps)(CountertopCabinetQuantity);
